// #1516 — member status as a dated, derived interval record.
//
// A `users/{uid}/statusChanges/{id}` row is append-only and carries an
// `effectiveAt` (the member-meaningful date) alongside `changedAt` (the server
// clock at the moment an officer saved it). Intervals of "was this member
// active" are DERIVED from those rows, never stored — a leave ends because a
// later ACTIVE row exists, not because anything wrote an end date.
//
// Two invariants every consumer depends on:
//
// 1. `effectiveAt` may be MISSING. The per-tenant backfill
//    (embertracking/bin/backfill-status-change-effective-dates.js) is a manual
//    run across 40+ projects, so every read path here falls back to
//    `effectiveAt ?? changedAt`. Never sort these rows in Firestore with
//    `orderBy('effectiveAt')` — Firestore silently excludes docs missing the
//    ordered field. Read the (tiny) subcollection unordered and sort here.
//
// 2. Rows are soft-deleted (`deletedAt`), never removed, so a deletion is
//    auditable and reversible. Every helper below drops tombstoned rows.
//
// Pure module: no firebase, no moment. `toDateValue` duck-types `.toDate()` so
// it accepts Firestore Timestamps, moments, JS Dates and ISO strings alike.

const DAY_MS = 86400000;

const DEFAULT_SUSPENDED = ['ON_LEAVE', 'MEDICAL_LEAVE'];
const DEFAULT_DISABLED = ['RESIGNED', 'TERMINATED'];

// Timestamp | moment | Date | ISO string | epoch ms | {_seconds} -> Date | null.
// Returns null for anything unparseable — including `new Date('garbage')`,
// which is silently NaN rather than throwing.
const toDateValue = value => {
    if (value === null || value === undefined) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'object' && typeof value.toDate === 'function') {
        try {
            const date = value.toDate();
            return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
        } catch (e) {
            return null;
        }
    }

    // A Timestamp that has been through JSON (api payloads, exported data).
    if (typeof value === 'object') {
        const seconds = typeof value._seconds === 'number' ? value._seconds
            : typeof value.seconds === 'number' ? value.seconds : null;
        if (seconds !== null) {
            const nanos = value._nanoseconds || value.nanoseconds || 0;
            return new Date(seconds * 1000 + Math.floor(nanos / 1e6));
        }
        return null;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? new Date(value) : null;
    }

    if (typeof value === 'string') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
};

const time = date => (date ? date.getTime() : 0);

// Whole days between two instants. Math.round (not floor) is load-bearing: our
// dates are local midnights, so a range spanning a DST boundary is 23 or 25
// hours long and floor() would lose a day twice a year.
const daysBetween = (start, end) => Math.max(0, Math.round((time(end) - time(start)) / DAY_MS));

// Drop tombstoned + undatable rows, normalize dates, sort ascending.
//
// Sort key mirrors resolveEffectiveStatus exactly (effectiveAt, then changedAt,
// then id) so "the last row" here and "the greatest row" there can never
// disagree.
const normalizeStatusChanges = rows => {
    const normalized = [];

    (rows || []).forEach(row => {
        if (!row || row.deletedAt) return;

        const changedAt = toDateValue(row.changedAt);
        const effectiveAt = toDateValue(row.effectiveAt) || changedAt;
        // A row with neither date can't be placed on a timeline. The backfill
        // reports these rather than guessing at one.
        if (!effectiveAt) return;

        normalized.push({
            ...row,
            id: row.id || null,
            effectiveAt,
            changedAt,
            inferred: row.effectiveAtInferred === true || !row.effectiveAt
        });
    });

    normalized.sort((a, b) =>
        time(a.effectiveAt) - time(b.effectiveAt) ||
        time(a.changedAt) - time(b.changedAt) ||
        String(a.id || '').localeCompare(String(b.id || '')));

    return normalized;
};

// The row in force at `at` — the greatest `effectiveAt <= at`.
//
// Ties break on `changedAt` (a later correction supersedes an earlier one for
// the same effective day) then on doc id (pure determinism). That tie-break is
// what makes the backfill safe: once `effectiveAt === changedAt` on every
// legacy row, this resolves byte-identically to the old last-write-wins mirror.
const resolveEffectiveStatus = (rows, options = {}) => {
    const {
        at = new Date(),
        startDate = null,
        suspendedStatuses = DEFAULT_SUSPENDED,
        disabledStatuses = DEFAULT_DISABLED
    } = options;

    const asOf = toDateValue(at) || new Date();
    const memberStart = toDateValue(startDate);
    const normalized = normalizeStatusChanges(rows);

    let resolved = null;
    for (const row of normalized) {
        if (time(row.effectiveAt) > time(asOf)) break;
        resolved = row;
    }

    if (!resolved) {
        // No qualifying row: the member has always been ACTIVE, dated from their
        // start date. Matches what the UI already assumes when the subcollection
        // is empty. `inferred` when we don't even have a start date to point at.
        return {
            status: 'ACTIVE',
            effectiveAt: memberStart,
            changedAt: null,
            changedBy: null,
            sourceId: null,
            archived: false,
            suspended: false,
            inferred: !memberStart
        };
    }

    return {
        status: resolved.status,
        effectiveAt: resolved.effectiveAt,
        changedAt: resolved.changedAt,
        changedBy: resolved.changedBy || null,
        sourceId: resolved.id || null,
        archived: disabledStatuses.includes(resolved.status),
        suspended: suspendedStatuses.includes(resolved.status),
        inferred: resolved.inferred === true
    };
};

// Was this member on the roster and not on leave / departed at `date`?
// Half-open: a leave effective Mar 1 means NOT active on Mar 1; an ACTIVE row
// effective Apr 1 means active on Apr 1.
const isActiveOn = (user, statusChanges, date, options = {}) => {
    const at = toDateValue(date) || new Date();
    const startDate = toDateValue(user?.startDate);

    // Before they joined they weren't active, whatever the rows say. Only
    // enforceable when we actually know the start date.
    if (startDate && time(at) < time(startDate)) return false;

    const resolved = resolveEffectiveStatus(statusChanges, {
        ...options,
        at,
        startDate: user?.startDate
    });

    return !resolved.archived && !resolved.suspended;
};

// Fast membership test over a precomputed interval list. Prefer this to
// scaling a total by `factor` when you can count events directly (#1515).
const intervalsContain = (intervals, date) => {
    const at = toDateValue(date);
    if (!at) return false;

    return (intervals || []).some(interval => {
        const start = toDateValue(interval?.start);
        const end = toDateValue(interval?.end);
        if (!start || !end) return false;
        return time(at) >= time(start) && time(at) < time(end);
    });
};

// The half-open [start, end) windows in which this member was active, clipped
// to the requested range.
//
// `start` defaults to the member's start date (or the first status row);
// `end` defaults to now. Rows dated BEFORE `start` are deliberately kept — the
// row in force at `start` sets the opening state, and dropping it is the
// classic interval-helper bug (a member who went on leave last year would come
// back as active for the whole reporting period).
const activeIntervals = (user, statusChanges, options = {}) => {
    const {
        suspendedStatuses = DEFAULT_SUSPENDED,
        disabledStatuses = DEFAULT_DISABLED
    } = options;

    const rows = normalizeStatusChanges(statusChanges);
    const startDate = toDateValue(user?.startDate);

    const rangeEnd = toDateValue(options.end) || new Date();
    const rangeStart = toDateValue(options.start)
        || startDate
        || (rows.length ? rows[0].effectiveAt : rangeEnd);

    // When the start date is unknown we can't say when membership began, so the
    // range's own start stands in — flagged, rather than implying a precision we
    // don't have. (#1480 added `serviceStartDate`, but that's the member's career
    // start, not this department's — it can't stand in here, and missing
    // department start dates are still unbackfilled.)
    const memberStartInferred = !startDate;
    const memberStart = startDate || rangeStart;

    const windowStart = time(memberStart) > time(rangeStart) ? memberStart : rangeStart;
    const totalDays = daysBetween(rangeStart, rangeEnd);

    const empty = {
        intervals: [],
        activeDays: 0,
        totalDays,
        factor: 0,
        inferred: memberStartInferred,
        joinedAt: startDate,
        leftAt: null
    };

    if (time(windowStart) >= time(rangeEnd)) return empty;

    const isActiveStatus = status =>
        !suspendedStatuses.includes(status) && !disabledStatuses.includes(status);

    // Opening state: the row in force at windowStart (may predate the range).
    let state = 'ACTIVE';
    let stateInferred = memberStartInferred;
    let leftAt = null;
    for (const row of rows) {
        if (time(row.effectiveAt) > time(windowStart)) break;
        state = row.status;
        stateInferred = row.inferred;
        leftAt = disabledStatuses.includes(row.status) ? row.effectiveAt : null;
    }

    const intervals = [];
    let openStart = isActiveStatus(state) ? windowStart : null;
    let openInferred = stateInferred;

    const closeAt = boundary => {
        if (!openStart) return;
        if (time(boundary) > time(openStart)) {
            intervals.push({start: openStart, end: boundary, inferred: openInferred});
        }
        openStart = null;
    };

    for (const row of rows) {
        if (time(row.effectiveAt) <= time(windowStart)) continue;
        if (time(row.effectiveAt) >= time(rangeEnd)) break;

        const active = isActiveStatus(row.status);
        if (active && !openStart) {
            openStart = row.effectiveAt;
            openInferred = row.inferred;
        } else if (!active && openStart) {
            closeAt(row.effectiveAt);
        }

        // A rehire is just another ACTIVE row — leftAt tracks the latest
        // departure and clears again the moment they come back.
        leftAt = disabledStatuses.includes(row.status) ? row.effectiveAt : null;
    }

    closeAt(rangeEnd);

    const activeDays = intervals.reduce((sum, i) => sum + daysBetween(i.start, i.end), 0);

    return {
        intervals,
        activeDays,
        totalDays,
        factor: totalDays ? activeDays / totalDays : 0,
        inferred: memberStartInferred || intervals.some(i => i.inferred),
        joinedAt: startDate,
        leftAt
    };
};

module.exports = {
    toDateValue,
    normalizeStatusChanges,
    resolveEffectiveStatus,
    isActiveOn,
    intervalsContain,
    activeIntervals
};
